import { useCallback } from 'react';
import { NavLink as NL, useLocation, useNavigate } from 'react-router-dom';

/*
  Custom navigation utilities to preserve search
*/

export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parses the current search params
  const searchParams = new URLSearchParams(location.search);

  const navigateWithSearch = useCallback(
    (
      { pathname, search = {} }: { pathname?: string; search?: Record<string, any> },
      opts?: { omit?: string[] },
    ) => {
      // Get the freshest search params at the time of navigation
      const currentSearchParams = new URLSearchParams(window.location.search);
      const freshSearch = Object.fromEntries(currentSearchParams.entries());

      // Merge the fresh search params with the provided search params
      const mergedSearch = { ...freshSearch, ...search };

      const target = new URLSearchParams(mergedSearch);

      // Adds or removes search params
      Object.entries(search).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          target.delete(key);
        } else {
          target.set(key, value?.trim?.() ?? value);
        }
      });

      // Optionally omit certain keys
      if (opts?.omit) {
        opts.omit.forEach((key) => target.delete(key));
      }

      navigate({
        pathname: pathname || location.pathname,
        search: target.toString(),
      });
    },
    [navigate, location.pathname],
  );

  return {
    search: Object.fromEntries(searchParams.entries()),
    navigate: navigateWithSearch,
  };
};

export const NavLink = ({
  to,
  omit = null,
  children,
  ...props
}: {
  to: { pathname: string; search?: Record<string, any> };
  omit?: string[] | null;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Merge existing search with the new search
  const targetSearch = new URLSearchParams(searchParams);
  if (to.search) {
    Object.entries(to.search).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        targetSearch.delete(key);
      } else {
        targetSearch.set(key, value);
      }
    });
  }

  // Optionally omit keys
  if (omit) {
    omit.forEach((key) => targetSearch.delete(key));
  }

  const search = targetSearch.toString();

  return (
    <NL to={{ pathname: to.pathname, search }} {...props}>
      {children}
    </NL>
  );
};
